import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Link } from 'react-router-dom';
import { subDays } from 'date-fns';
import { COBStep } from '~/client-flow/clientOb/ClientOnboarding.utils';
import { routes } from '~/constants';
import { Intent } from '~/infra/access-control';
import { relationshipSummaryModel } from '~/models';
import { RelationshipProgressStatus } from '~/models/relationship';
import { fromDateStampObject } from '~/models/time';
import { formatShortMonthUSDate } from '~/utils';
export const proposalsDashboardTextsPath = 'pages.proposalsDashboard';
const fiveDays = 1000 * 60 * 60 * 24 * 5;
const tenDays = fiveDays * 2;
const didTimePassed = (timestamp, time)=>{
    const timePassed = Date.now() - timestamp;
    return timePassed > time;
};
const statusToSeverity = {
    withdrawnForEditing: 11,
    microDepositStarted: 10,
    microDepositFailed: 9,
    paymentMethod: 8,
    reviewed: 7,
    sent: 6,
    expired: 5
};
export const proposalStatusDefinition = (isVendor, proposalsTexts)=>{
    return {
        withdrawnForEditing: {
            severity: statusToSeverity.withdrawnForEditing,
            statusText: proposalsTexts.statusLabels.pendingRepublishing(),
            badgeSkin: 'warning'
        },
        sent: {
            severity: statusToSeverity.sent,
            statusText: isVendor ? proposalsTexts.statusLabels.proposalSentToClient() : proposalsTexts.statusLabels.sent(),
            badgeSkin: 'default'
        },
        reviewed: {
            severity: statusToSeverity.reviewed,
            statusText: isVendor ? proposalsTexts.statusLabels.reviewedByClient() : proposalsTexts.statusLabels.reviewed(),
            badgeSkin: 'default'
        },
        microDepositFailed: {
            severity: statusToSeverity.microDepositFailed,
            statusText: proposalsTexts.statusLabels.microDepositFailed(),
            badgeSkin: 'error'
        },
        microDepositStarted: {
            severity: statusToSeverity.microDepositStarted,
            statusText: proposalsTexts.statusLabels.initiatedMicroDeposit(),
            badgeSkin: 'default'
        },
        paymentMethod: {
            severity: statusToSeverity.paymentMethod,
            statusText: proposalsTexts.statusLabels.connectedPaymentMethod(),
            badgeSkin: 'default'
        },
        expired: {
            severity: statusToSeverity.expired,
            statusText: proposalsTexts.statusLabels.expired(),
            badgeSkin: 'default'
        }
    };
};
const isStatusMicroDepositFailed = (agreement)=>(agreement === null || agreement === void 0 ? void 0 : agreement.progressStatus) === RelationshipProgressStatus.MicroDeposit && didTimePassed(agreement === null || agreement === void 0 ? void 0 : agreement.progressStatusTimestamp, tenDays);
export const getAgreementStatus = (agreement)=>{
    const { progressStatus, progressStatusTimestamp } = agreement;
    const shouldOverrideOriginalStatus = isStatusMicroDepositFailed(agreement);
    if (shouldOverrideOriginalStatus) {
        const mdDate = new Date(progressStatusTimestamp);
        mdDate.setDate(mdDate.getDate() + 10);
        const newTimestamp = mdDate.getTime();
        return {
            progressStatus: RelationshipProgressStatus.MicroDepositFailed,
            progressStatusTimestamp: newTimestamp
        };
    }
    return {
        progressStatus,
        progressStatusTimestamp
    };
};
export const getSentByUIData = (agreement)=>{
    const { seller } = agreement;
    const sellerDisplayValue = "".concat(seller.firstName, " ").concat(seller.lastName);
    return sellerDisplayValue;
};
export const getStatusUIData = (agreement, isVendor, proposalsTexts)=>{
    const { progressStatusTimestamp, expirationDate } = agreement;
    const statusDefinition = proposalStatusDefinition(isVendor, proposalsTexts);
    if (agreement.withdrawnForEditing) {
        const lastUpdateText = progressStatusTimestamp ? proposalsTexts.statusLabels.lastUpdate({
            date: formatShortMonthUSDate(new Date(progressStatusTimestamp))
        }) : '';
        return _object_spread_props(_object_spread({}, statusDefinition.withdrawnForEditing), {
            lastUpdateText,
            viewableStatus: 'withdrawnForEditing'
        });
    }
    const progressStatus = agreement.progressStatus;
    let lastUpdateText = expirationDate ? proposalsTexts.statusLabels.validUntil({
        date: formatShortMonthUSDate(subDays(fromDateStampObject(expirationDate), 1))
    }) : progressStatusTimestamp ? proposalsTexts.statusLabels.lastUpdate({
        date: formatShortMonthUSDate(new Date(progressStatusTimestamp))
    }) : '';
    let viewableStatus;
    switch(progressStatus){
        case RelationshipProgressStatus.MicroDeposit:
            {
                viewableStatus = isStatusMicroDepositFailed(agreement) ? 'microDepositFailed' : 'microDepositStarted';
                lastUpdateText = viewableStatus === 'microDepositFailed' && progressStatusTimestamp ? proposalsTexts.statusLabels.lastUpdate({
                    date: formatShortMonthUSDate(new Date(progressStatusTimestamp + tenDays))
                }) : lastUpdateText;
                break;
            }
        case RelationshipProgressStatus.Expired:
            {
                viewableStatus = progressStatus;
                lastUpdateText = proposalsTexts.statusLabels.expiredOn({
                    date: formatShortMonthUSDate(fromDateStampObject(expirationDate))
                });
                break;
            }
        default:
            viewableStatus = progressStatus;
            break;
    }
    return _object_spread_props(_object_spread({}, statusDefinition[viewableStatus]), {
        lastUpdateText,
        viewableStatus
    });
};
const sortByCompanyName = function sortByCompanyName() {
    let isVendor = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : false;
    return (a, b)=>{
        const nameA = relationshipSummaryModel.opposingPartyName(a, isVendor).toLowerCase();
        const nameB = relationshipSummaryModel.opposingPartyName(b, isVendor).toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    };
};
const sortById = (a, b)=>a.id < b.id ? -1 : 1;
const sortBySentDate = (a, b)=>b.createdOn.getTime() - a.createdOn.getTime();
const sortBySentBy = (a, b)=>{
    const sellerA = getSentByUIData(a);
    const sellerB = getSentByUIData(b);
    if (sellerA === sellerB) {
        return 0;
    } else if (sellerA > sellerB) {
        return 1;
    } else {
        return -1;
    }
};
const sortByStatus = (a, b, isVendor, proposalsTexts)=>{
    const { severity: severityA } = getStatusUIData(a, isVendor, proposalsTexts);
    const { severity: severityB } = getStatusUIData(b, isVendor, proposalsTexts);
    if (severityA < severityB) {
        return -1;
    }
    if (severityA > severityB) {
        return 1;
    }
    return 0;
};
const sortByStatusUpdate = (a, b)=>{
    if (!a.progressStatusTimestamp && !b.progressStatusTimestamp) {
        return 0;
    }
    if (!a.progressStatusTimestamp) {
        return 1;
    }
    if (!b.progressStatusTimestamp) {
        return -1;
    }
    return b.progressStatusTimestamp - a.progressStatusTimestamp;
};
export const sortFunctions = {
    0: (param)=>{
        let { isAscending, isVendor } = param;
        return (a, b)=>{
            const retval = sortByCompanyName(isVendor)(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    1: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortBySentDate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    2: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const sentBySort = sortBySentBy(a, b);
            return isAscending ? -sentBySort : sentBySort;
        };
    },
    3: (param)=>{
        let { isAscending, isVendor, proposalsTexts } = param;
        return (a, b)=>{
            const statusSort = sortByStatus(a, b, isVendor, proposalsTexts);
            return (isAscending ? -statusSort : statusSort) || sortByStatusUpdate(a, b) || sortById(a, b);
        };
    },
    4: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByStatusUpdate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    }
};
export function getProposalsStatusCounters(agreements, isVendor, proposalsTexts) {
    return agreements.reduce((acc, agreement)=>{
        const statusUI = getStatusUIData(agreement, isVendor, proposalsTexts);
        acc[statusUI.viewableStatus] = (acc[statusUI.viewableStatus] || 0) + 1;
        return acc;
    }, {});
}
export function getRowAndMainAction(param) {
    let { proposal, isVendor, isViewMode, heapId, navigate, openClientPreview, texts } = param;
    if (isVendor || isViewMode) {
        return {
            rowProps: {
                as: 'button',
                onClick: openClientPreview
            },
            mainAction: {
                buttonText: isViewMode ? texts.mainAction.view() : texts.mainAction.preview(),
                intent: isViewMode ? Intent.ClientViewProposal : undefined,
                onClickButton: openClientPreview
            }
        };
    } else {
        const clientAgreementLink = routes.clientOnboarding(proposal.id, isStatusMicroDepositFailed(proposal) ? COBStep.paymentMethod : undefined);
        return {
            rowProps: {
                as: Link,
                to: clientAgreementLink
            },
            mainAction: {
                buttonText: texts.mainAction.view(),
                intent: Intent.ClientViewProposal,
                btnHeapId: "".concat(heapId, "-view-btn"),
                onClickButton: ()=>navigate(clientAgreementLink)
            }
        };
    }
}
